import React, { useState, useEffect } from 'react';
import { LifeEvent } from '../../services/lifeEventsService';
import { lifeEventsService } from '../../services/lifeEventsService';
import './LifeEvents.css';

interface LifeEventFormProps {
  event: LifeEvent | null;
  onClose: () => void;
  onSave: () => void;
}

const LifeEventForm: React.FC<LifeEventFormProps> = ({ event, onClose, onSave }) => {
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setDescription(event.description || '');
      // Format date for input (YYYY-MM-DD)
      const date = new Date(event.event_date);
      setEventDate(date.toISOString().split('T')[0]);
    } else {
      // Default to today's date for new events
      const today = new Date();
      setEventDate(today.toISOString().split('T')[0]);
    }
  }, [event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!eventDate) {
      setError('Event date is required');
      return;
    }

    setSaving(true);
    try {
      if (event) {
        // Update existing event
        const response = await lifeEventsService.updateLifeEvent(event.id, {
          title: title.trim(),
          description: description.trim() || undefined,
          event_date: eventDate
        });
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to update event');
        }
      } else {
        // Create new event
        const response = await lifeEventsService.createLifeEvent({
          title: title.trim(),
          description: description.trim() || undefined,
          event_date: eventDate
        });
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to create event');
        }
      }
      onSave();
    } catch (err: any) {
      console.error('Error saving event:', err);
      setError(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="life-events-form-overlay" onClick={onClose}>
      <div className="life-events-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="life-events-form-header">
          <h2>{event ? 'Edit Event' : 'Add Event'}</h2>
          <button onClick={onClose} className="life-events-form-close">×</button>
        </div>

        <form onSubmit={handleSubmit} className="life-events-form">
          {error && <div className="life-events-form-error">{error}</div>}

          <div className="life-events-form-group">
            <label htmlFor="title">Title *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter event title"
              className="life-events-form-input"
              required
            />
          </div>

          <div className="life-events-form-group">
            <label htmlFor="event_date">Event Date *</label>
            <input
              id="event_date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="life-events-form-input"
              required
            />
          </div>

          <div className="life-events-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter event description (optional)"
              className="life-events-form-textarea"
              rows={4}
            />
          </div>

          <div className="life-events-form-actions">
            <button
              type="button"
              onClick={onClose}
              className="life-events-form-cancel-btn"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="life-events-form-submit-btn"
              disabled={saving}
            >
              {saving ? 'Saving...' : (event ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LifeEventForm;
